import {
    DialogConfirmOptions, IGridPagedResult, MessageType
} from '@caslink/common-client';

import { Injectable } from '@angular/core';
import { SubscriptionTypes } from '@caslink/common-client/ng-app/alerts/typings/SubscriptionTypes';
import { DialogService } from '@caslink/common-client/ng-app/dialog-service/dialog.service';
import { NotificationService } from '@caslink/common-client/ng-app/notification/NotificationService';
import { ProxyService } from '@caslink/common-client/ng-app/proxy/ProxyService';
import { IChangeCommunicationModuleRequest } from '../models/change-communication-module-request';
import { DeviceLocationDetail } from '../models/device-location-detail.interface';
import { IDeviceStatusListSearchCriteria } from '../models/device-status-list-search-criteria.interface';
import { IDeviceStatusListSummary } from '../models/device-status-list-summary.interface';
import { IDeviceStatusBulkTemplateUpdateResult } from '../models/models';

@Injectable({
    providedIn: 'root'
})
export class DeviceStatusService {
    public static $name: string = 'deviceStatusService';

    constructor(
        private proxyService: ProxyService,
        private notificationService: NotificationService,
        private dialogService: DialogService
    ) {
    }

    public getList(query: IDeviceStatusListSearchCriteria):
        angular.IPromise<IGridPagedResult<IDeviceStatusListSummary>> {

        return this.proxyService.request({
            endPoint: 'deviceStatus',
            queryParams: this.getQueryParams(query),
            route: 'getDeviceStatuses'
        }
        ).as<IGridPagedResult<IDeviceStatusListSummary>>();
    }

    public getPins(query: IDeviceStatusListSearchCriteria):
        angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.LocationPinSearchResultViewModel[]> {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            queryParams: this.getQueryParams(query, false),
            route: 'getPinList'
        }).asArray<CASLink.Device.Ui.ViewModel.Modules.LocationPinSearchResultViewModel>();
    }

    public delete(item: IDeviceStatusListSummary): angular.IPromise<void> {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            queryParams: {
                'deviceHardwareItemIdentifier.category': item.category,
                'deviceHardwareItemIdentifier.moduleId': item.hardwareItem.moduleId,
                'deviceHardwareItemIdentifier.sequence': item.sequence
            },
            route: 'deleteDevice'
        }
        ).as<void>();
    }

    public save(item: IDeviceStatusListSummary): angular.IPromise<IDeviceStatusListSummary> {
        return this.proxyService.request({
            data: item,
            endPoint: 'deviceStatus',
            route: 'saveDevice'
        }
        ).as<IDeviceStatusListSummary>();
    }

    public startStatusUpdates(
        loadedLocationId: number,
        items: IDeviceStatusListSummary[],
        locationId: number): void {
        if (items) {
            items.forEach((item: IDeviceStatusListSummary) =>
                this.startStatusUpdate(loadedLocationId, item, locationId));
        }
    }

    public stopStatusUpdates(loadedLocationId: number, items: IDeviceStatusListSummary[], locationId: number): void {
        if (items) {
            items.forEach((item: IDeviceStatusListSummary) =>
                this.stopStatusUpdate(loadedLocationId, item, locationId));
        }
    }

    public changeCommModule(item: IChangeCommunicationModuleRequest):
        angular.IPromise<IDeviceStatusListSummary> {
        return this.proxyService.request({
            data: item,
            endPoint: 'deviceStatus',
            route: 'changeCommModule'
        }
        ).as<IDeviceStatusListSummary>();
    }

    public replaceModule(item: CASLink.Device.Ui.ViewModel.Modules.ReplaceModuleRequest):
        angular.IPromise<void> {
        return this.proxyService.request({
            data: item,
            endPoint: 'deviceStatus',
            route: 'replaceModule'
        }
        ).as<void>();
    }

    public getDeviceJson(hardwareItemId: number, category: string): angular.IPromise<string> {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            queryParams: {
                category,
                hardwareItemId
            },
            route: 'getDeviceJson'
        }).as<string>();
    }

    public setTemplateVersion(
        searchCriteria: IDeviceStatusListSearchCriteria,
        bulkTemplateUpdate: IDeviceStatusBulkTemplateUpdateResult
    ): angular.IPromise<void> {
        const locationId = typeof searchCriteria.locationId !== 'number' && searchCriteria.locationId ?
            Number(searchCriteria.locationId) : searchCriteria.locationId;
        const moduleId = typeof searchCriteria.moduleId !== 'number' && searchCriteria.moduleId ?
            Number(searchCriteria.moduleId) : searchCriteria.moduleId;
        const jobNumber = typeof searchCriteria.jobNumber !== 'number' && searchCriteria.jobNumber ?
            Number(searchCriteria.jobNumber) : searchCriteria.jobNumber;
        return this.proxyService.request({
            data: {
                hardwareCategory: searchCriteria.hardwareCategory,
                jobNumber: jobNumber,
                locationId: locationId,
                moduleId: moduleId,
                wildcard: searchCriteria.wildcard
            },
            endPoint: 'deviceStatus',
            route: 'setTemplateVersion',
            routeParams: {
                hardwareVersion: bulkTemplateUpdate.hardwareVersion,
                modbusId: bulkTemplateUpdate.modbusId,
                templateId: bulkTemplateUpdate.configurationId
            }
        }).as<void>();
    }

    public refreshHardwareConfiguration(item: IDeviceStatusListSummary): angular.IPromise<void> {
        return this.proxyService.request({
            data: item,
            endPoint: 'deviceStatus',
            route: 'refreshHardwareConfiguration'
        }).as<void>();
    }

    public getDetailLocationView(query: IDeviceStatusListSearchCriteria): angular.IPromise<DeviceLocationDetail> {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'getDetailLocationView',
            queryParams: this.getQueryParams(query),
        }).as<DeviceLocationDetail>();
    }

    public deviceStatusUpdate(
        devices: IDeviceStatusListSummary[],
        data: IDeviceStatusListSummary
    ) {
        for (const item of devices) {
            if (item.$isInEditMode) {
                return;
            }

            if (item.hardwareItem.id === data.hardwareItem.id) {
                item.configuration = data.configuration;
                item.deviceStatusType = data.deviceStatusType;
                item.componentStatuses = data.componentStatuses;
                item.hardwareVersion = data.hardwareVersion;
                item.isVersionMismatch = data.isVersionMismatch;
                item.lastUpdated = data.lastUpdated;
                item.agent = data.agent;
                item.firmwareUpdateStatus = data.firmwareUpdateStatus;
                item.embeddedVersion = data.embeddedVersion;
                item.pendingFirmwareVersion = data.pendingFirmwareVersion;
            }
        }
    }

    public promptCommModuleChange(
        item: IChangeCommunicationModuleRequest,
        action: (success: boolean) => void,
        rejectAction?: () => void) {
        const options = {
            confirmBtnText: 'Yes',
            message: 'Would you really like to change Communication Module of this Device?',
            messageType: MessageType.Warning,
            showCancelBtn: true,
            title: 'Save?'
        } as DialogConfirmOptions;

        return this.dialogService.showConfirmDialog({data: options})
            .afterClosed()
            .toPromise()
            .then(confirmed => {
                if (confirmed) {
                    this.changeCommModule(item)
                        .then(() => action(true))
                        .catch(() => action(false));
                }
            })
            .catch(() => {
                if (rejectAction) { rejectAction(); }
            });
    }

    public updateFirmwareVersion(searchCriteria: IDeviceStatusListSearchCriteria, firmwareVersion: string) {
        const locationId = typeof searchCriteria.locationId !== 'number' && searchCriteria.locationId ?
            Number(searchCriteria.locationId) : searchCriteria.locationId;
        const moduleId = typeof searchCriteria.moduleId !== 'number' && searchCriteria.moduleId ?
            Number(searchCriteria.moduleId) : searchCriteria.moduleId;
        const jobNumber = typeof searchCriteria.jobNumber !== 'number' && searchCriteria.jobNumber ?
            Number(searchCriteria.jobNumber) : searchCriteria.jobNumber;
        return this.proxyService.request({
            data: {
                hardwareCategory: searchCriteria.hardwareCategory,
                jobNumber: jobNumber,
                locationId: locationId,
                moduleId: moduleId,
                wildcard: searchCriteria.wildcard
            },
            endPoint: 'deviceStatus',
            route: 'updateFirmwareVersion',
            routeParams: {
                firmwareVersion: firmwareVersion
            }
        }).as<void>();
    }

    public canUpdateFirmwareVersion(hardwareCategory: string): angular.IPromise<boolean> {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'canUpdateFirmwareVersion',
            routeParams: {
                hardwareCategory: hardwareCategory
            }
        }).as<boolean>();
    }

    public rotateEncryptionKeys(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'rotateEncryptionKeys',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    public pingAgent(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'pingAgent',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    public hardReboot(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'hardReboot',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    public softReboot(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'softReboot',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    public restartAgent(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'restartAgent',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    public restartApplication(hardwareItemId: number) {
        return this.proxyService.request({
            endPoint: 'deviceStatus',
            route: 'restartApplication',
            routeParams: {
                hardwareItemId: hardwareItemId
            }
        }).asNoContent();
    }

    private startStatusUpdate(loadedLocationId: number, item: IDeviceStatusListSummary, locationId: number): void {
        this.notificationService.startUpdates(loadedLocationId, SubscriptionTypes.DeviceStatus, {
            hardwareItemId: item.hardwareItem.id,
        }, item);
    }

    private stopStatusUpdate(loadedLocationId: number, item: IDeviceStatusListSummary, locationId: number): void {
        this.notificationService.stopUpdates(loadedLocationId, SubscriptionTypes.DeviceStatus, {
            hardwareItemId: item.hardwareItem.id,
        });
    }

    private getQueryParams(query: IDeviceStatusListSearchCriteria, overrideLocation: boolean = true) {
        const sort = (query.ascending) ? 0 : 1;
        let locationId = typeof query.locationId !== 'number' && query.locationId != null ? Number(query.locationId) : query.locationId;
        const moduleId = typeof query.moduleId !== 'number' && query.moduleId != null ? Number(query.moduleId) : query.moduleId;
        const jobNumber = typeof query.jobNumber !== 'number' && query.jobNumber != null ? Number(query.jobNumber) : query.jobNumber;

        if (query.pinLocationId && overrideLocation) {
            locationId = typeof query.pinLocationId !== 'number' && query.pinLocationId != null ?
            Number(query.pinLocationId) : query.pinLocationId;
        }

        const params = {
            'searchCriteria.hardwareCategory': query.hardwareCategory,
            'searchCriteria.jobNumber': jobNumber,
            'searchCriteria.locationId': locationId,
            'searchCriteria.moduleId': moduleId,
            'searchCriteria.moduleOrLocationOrJobNumber': query.moduleOrLocationOrJobNumber,
            'searchCriteria.pagingCriteria.includeTotalCount': query.includeTotalCount,
            'searchCriteria.pagingCriteria.skip': (query.page - 1) * query.limit,
            'searchCriteria.pagingCriteria.sortDirection': sort,
            'searchCriteria.pagingCriteria.sortExpressions': [query.getOrderWithCapitalized()],
            'searchCriteria.pagingCriteria.take': query.limit,
            'searchCriteria.reportTemplateId': query.reportTemplateId,
            'searchCriteria.wildcard': query.wildcard,
            'searchCriteria.customerId': query.customerId,
            'searchCriteria.IsCertified': query.isCertified,
            'searchCriteria.IsAirQualityExcellence': query.isAirQualityExcellence,
        };
        return params;
    }

}
